import type { UserRole } from '@fig/shared';
import { Activity, Database, Download, Mail, Plus, Trash2, UserCog } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { ErrorState } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { StatusBanner } from '@/components/ui/status-banner';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { TruncatedTooltip } from '@/components/ui/truncated-tooltip';
import {
  useAdminRoles,
  useSaveAdminRoles,
  type AdminRoleAssignment,
} from '@/hooks/api/useAdminRoles';
import {
  useAdminTeamMappings,
  useSaveAdminTeamMappings,
  type AdminManagedTeam,
} from '@/hooks/api/useAdminTeamMappings';
import {
  useDbStatus,
  useHealthConsole,
  useIngestionStatus,
  useSyncStatus,
  type IngestionStatusResponse,
} from '@/hooks/api/usePipeline';
import { formatDateTime, formatDuration, formatNumber, formatRelativeTime } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { useToast } from '@/providers/toast-provider';

import { AdminSkeleton } from './AdminSkeleton';
import { FirelightPrefillTab } from './FirelightPrefillTab';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
type SyncBadgeVariant = 'success' | 'warning' | 'destructive' | 'secondary';
type AdminTabValue = 'roles' | 'teams' | 'syncs' | 'health' | 'prefill';
type IngestionStatusLatestRun = NonNullable<IngestionStatusResponse['latestRun']>;
type DraftRoleAssignment = Pick<
  AdminRoleAssignment,
  'email' | 'role' | 'hasTeamAssignment' | 'salesTeamNames'
> & {
  draftId: string;
};
let draftRoleIdCounter = 0;

export function AdminPage() {
  const [activeTab, setActiveTab] = useState<AdminTabValue>('roles');
  const [draftRoles, setDraftRoles] = useState<DraftRoleAssignment[]>([]);
  const [draftEmailsByTeam, setDraftEmailsByTeam] = useState<Record<string, string>>({});
  const [editingTeam, setEditingTeam] = useState<string | null>(null);
  const { showToast } = useToast();

  const saveRolesMutation = useSaveAdminRoles();
  const saveMappingsMutation = useSaveAdminTeamMappings();

  const {
    data: syncStatus,
    isLoading: syncLoading,
    error: syncError,
    refetch: refetchSync,
  } = useSyncStatus();

  const {
    data: adminRoles,
    isLoading: adminRolesLoading,
    error: adminRolesError,
    refetch: refetchAdminRoles,
  } = useAdminRoles();

  const {
    data: adminTeams,
    isLoading: adminTeamsLoading,
    error: adminTeamsError,
    refetch: refetchAdminTeams,
  } = useAdminTeamMappings();

  const {
    data: ingestionStatus,
    isLoading: ingestionLoading,
    error: ingestionError,
    refetch: refetchIngestion,
  } = useIngestionStatus();

  const {
    data: dbStatus,
    isLoading: dbStatusLoading,
    error: dbStatusError,
    refetch: refetchDbStatus,
  } = useDbStatus();
  const {
    data: healthConsole,
    isLoading: healthLoading,
    error: healthError,
    refetch: refetchHealth,
  } = useHealthConsole({
    enabled: activeTab === 'health',
    refetchInterval: activeTab === 'health' ? 15_000 : false,
  });

  useEffect(() => {
    if (!adminTeams) return;

    setDraftEmailsByTeam(buildDraftEmailsByTeam(adminTeams.teams));
  }, [adminTeams]);

  useEffect(() => {
    if (!adminRoles) return;

    setDraftRoles(buildDraftRoles(adminRoles.roles));
  }, [adminRoles]);

  const roleValidation = useMemo(() => validateRoleAssignments(draftRoles), [draftRoles]);

  const rolesDirty = useMemo(() => {
    if (!adminRoles) return false;
    return serializeRoleAssignments(draftRoles) !== serializeRoleAssignments(adminRoles.roles);
  }, [adminRoles, draftRoles]);

  const validation = useMemo(
    () => validateTeamEmails(adminTeams?.teams ?? [], draftEmailsByTeam),
    [adminTeams?.teams, draftEmailsByTeam]
  );

  const isDirty = useMemo(() => {
    if (!adminTeams) return false;

    return adminTeams.teams.some((team) => {
      const current = parseEmailTokens(draftEmailsByTeam[team.salesTeamId] ?? '');
      const saved = [...team.assignedEmails].sort((a, b) => a.localeCompare(b));
      return serializeEmails(current) !== serializeEmails(saved);
    });
  }, [adminTeams, draftEmailsByTeam]);

  const handleDraftChange = (salesTeamId: string, value: string) => {
    setDraftEmailsByTeam((current) => ({
      ...current,
      [salesTeamId]: value,
    }));
  };

  const handleRoleEmailChange = (index: number, value: string) => {
    setDraftRoles((current) =>
      current.map((assignment, assignmentIndex) =>
        assignmentIndex === index ? { ...assignment, email: value } : assignment
      )
    );
  };

  const handleRoleChange = (index: number, role: UserRole) => {
    setDraftRoles((current) =>
      current.map((assignment, assignmentIndex) =>
        assignmentIndex === index ? { ...assignment, role } : assignment
      )
    );
  };

  const handleAddRole = () => {
    setDraftRoles((current) => [
      ...current,
      {
        draftId: createDraftRoleId(),
        email: '',
        role: 'vp',
        hasTeamAssignment: false,
        salesTeamNames: [],
      },
    ]);
  };

  const handleRemoveRole = (index: number) => {
    setDraftRoles((current) => current.filter((_, assignmentIndex) => assignmentIndex !== index));
  };

  const handleSaveRoles = () => {
    if (roleValidation.hasErrors) {
      return;
    }

    saveRolesMutation.mutate(
      draftRoles.map((assignment) => ({
        email: normalizeEmail(assignment.email) ?? assignment.email,
        role: assignment.role,
      })),
      {
        onSuccess: (result) => {
          setDraftRoles(buildDraftRoles(result.roles));
          showToast({
            title: `Saved ${result.summary.totalCount} role assignments.`,
          });
        },
      }
    );
  };

  const handleSaveMappings = () => {
    if (!adminTeams || validation.hasErrors) {
      return;
    }

    saveMappingsMutation.mutate(
      adminTeams.teams.map((team) => ({
        salesTeamId: team.salesTeamId,
        emails: parseEmailTokens(draftEmailsByTeam[team.salesTeamId] ?? ''),
      })),
      {
        onSuccess: (result) => {
          setDraftEmailsByTeam(buildDraftEmailsByTeam(result.teams));
          setEditingTeam(null);
          showToast({
            title: `Saved ${result.summary.assignedEmailCount} VP email assignments across ${result.summary.teamCount} teams.`,
          });
        },
      }
    );
  };

  if (
    syncLoading ||
    adminRolesLoading ||
    adminTeamsLoading ||
    ingestionLoading ||
    dbStatusLoading
  ) {
    return <AdminSkeleton />;
  }

  const pageError =
    syncError instanceof Error
      ? syncError
      : adminRolesError instanceof Error
        ? adminRolesError
        : adminTeamsError instanceof Error
          ? adminTeamsError
          : ingestionError instanceof Error
            ? ingestionError
            : dbStatusError instanceof Error
              ? dbStatusError
              : null;

  if (pageError) {
    return (
      <div className="py-8">
        <ErrorState
          message={pageError.message}
          onRetry={() => {
            refetchSync();
            refetchAdminRoles();
            refetchAdminTeams();
            refetchIngestion();
            refetchDbStatus();
          }}
        />
      </div>
    );
  }

  const teams = adminTeams?.teams ?? [];
  const latestPolicyRun = getLatestSyncRun(dbStatus?.recentSyncHistory ?? [], 'policies');
  const latestFirelightMappingRun = getLatestSyncRun(
    dbStatus?.recentFirelightMappingRuns ?? [],
    'firelight_mappings'
  );
  const latestScoringRun = (dbStatus?.recentScoringJobs?.[0] ?? null) as Record<
    string,
    unknown
  > | null;
  const latestArwRun =
    ingestionStatus?.latestRun ??
    ((dbStatus?.recentIngestionLogs?.[0] ?? null) as IngestionStatusLatestRun | null);
  const arwStatus = getJobStatusBadge(latestArwRun?.status ?? null);
  const policyStatus = getJobStatusBadge((latestPolicyRun?.status as string | undefined) ?? null);
  const firelightMappingStatus = getJobStatusBadge(
    (latestFirelightMappingRun?.status as string | undefined) ?? null
  );
  const scoringStatus = getJobStatusBadge((latestScoringRun?.status as string | undefined) ?? null);
  const scheduledJobs = [
    {
      job: 'ARW rate ingestion',
      schedule: 'Hourly on the hour',
      lastRun: formatDateTime(latestArwRun?.started_at ?? null),
      status: arwStatus,
      notes: 'Imports carrier products and rates from ARW.',
    },
    {
      job: 'Scoring algorithm',
      schedule: '6:15 AM and 12:15 PM ET',
      lastRun: formatDateTime(
        (latestScoringRun?.started_at ?? latestScoringRun?.startedAt) as string | null
      ),
      status: scoringStatus,
      notes: 'Refreshes policies, clients, and parties, then scores opportunities.',
    },
    {
      job: 'Firelight/ARW mapping sync',
      schedule: '6:15 AM and 12:15 PM ET',
      lastRun: formatDateTime((latestFirelightMappingRun?.created_at ?? null) as string | null),
      status: firelightMappingStatus,
      notes: 'Matches ARW products to FIG Products/Firelight application metadata.',
    },
  ];
  const tabs: { label: string; value: AdminTabValue }[] = [
    { label: 'Roles', value: 'roles' },
    { label: 'Teams', value: 'teams' },
    { label: 'Syncs', value: 'syncs' },
    { label: 'Health', value: 'health' },
    { label: 'Prefill', value: 'prefill' },
  ];

  return (
    <Tabs
      value={activeTab}
      onValueChange={(value) => setActiveTab(value as AdminTabValue)}
      className="space-y-4"
    >
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-semibold tracking-tight">Operations</h1>
          <div
            className="inline-flex h-7 items-center rounded-md p-0.5"
            style={{ backgroundColor: 'hsl(var(--muted))' }}
          >
            {tabs.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setActiveTab(tab.value)}
                className={cn(
                  'h-6 rounded px-2 text-xs font-medium transition-colors',
                  activeTab === tab.value
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                style={
                  activeTab === tab.value
                    ? { backgroundColor: 'hsl(var(--background))' }
                    : undefined
                }
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <p className="text-sm text-muted-foreground">
          Manage VP team access and review the Snowflake-backed data pipelines that keep the app
          fresh.
        </p>
      </div>

      <TabsContent value="roles" className="space-y-4">
        <Card className="bg-surface-0" style={{ boxShadow: 'var(--shadow-1)' }}>
          <CardHeader className="space-y-2">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <UserCog className="h-4 w-4 text-primary" />
                  <CardTitle>Role Management</CardTitle>
                </div>
                <CardDescription>
                  Assign each login email to Admin or Sales VP. Sales VPs still need a team
                  assignment on the Teams tab before they can see opportunity data.
                </CardDescription>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button size="lg" variant="outline" onClick={handleAddRole}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Role
                </Button>
                <Button
                  size="lg"
                  onClick={handleSaveRoles}
                  disabled={!rolesDirty || roleValidation.hasErrors || saveRolesMutation.isPending}
                  style={{ backgroundColor: 'hsl(211 47% 20%)' }}
                  className="min-w-[9.5rem] text-white shadow-sm hover:opacity-90"
                >
                  {saveRolesMutation.isPending ? 'Saving...' : 'Save Roles'}
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            {saveRolesMutation.isError && (
              <StatusBanner
                variant="error"
                title={
                  saveRolesMutation.error instanceof Error
                    ? saveRolesMutation.error.message
                    : 'Failed to save role assignments'
                }
              />
            )}

            {roleValidation.hasErrors && (
              <StatusBanner
                variant="error"
                title="Fix invalid or duplicate role assignments before saving."
              />
            )}

            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-primary/[0.04] text-left text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
                    <th className="px-3 py-2.5">Email</th>
                    <th className="w-44 px-3 py-2.5">Role</th>
                    <th className="w-56 px-3 py-2.5">VP Team</th>
                    <th className="w-10 px-2 py-2.5">
                      <span className="sr-only">Remove</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {draftRoles.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-3 py-8 text-center text-sm text-muted-foreground"
                      >
                        No role assignments yet.
                      </td>
                    </tr>
                  ) : (
                    draftRoles.map((assignment, index) => {
                      const normalizedEmail = normalizeEmail(assignment.email) ?? '';
                      const invalid = roleValidation.invalidEmails.includes(normalizedEmail);
                      const duplicate = roleValidation.duplicateEmails.includes(normalizedEmail);
                      const needsTeam = assignment.role === 'vp' && !assignment.hasTeamAssignment;

                      return (
                        <tr key={assignment.draftId} className="group border-b last:border-0">
                          <td className="min-w-[18rem] px-3 py-2.5 align-top">
                            <Input
                              value={assignment.email}
                              onChange={(event) => handleRoleEmailChange(index, event.target.value)}
                              placeholder="person@figmarketing.com"
                              className={cn(
                                'h-9 bg-background',
                                (invalid || duplicate) &&
                                  'border-destructive focus-visible:ring-destructive'
                              )}
                            />
                            {(invalid || duplicate) && (
                              <p className="mt-1 text-xs text-destructive">
                                {invalid
                                  ? 'Enter a valid email.'
                                  : 'Email is assigned more than once.'}
                              </p>
                            )}
                          </td>
                          <td className="px-3 py-2.5 align-top">
                            <Select
                              value={assignment.role}
                              onValueChange={(value) => handleRoleChange(index, value as UserRole)}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="vp">Sales VP</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-3 py-2.5 align-top">
                            {assignment.role === 'admin' ? (
                              <Badge variant="secondary">All access</Badge>
                            ) : assignment.hasTeamAssignment ? (
                              <div className="flex flex-wrap gap-1">
                                {assignment.salesTeamNames.length > 0 ? (
                                  assignment.salesTeamNames.map((teamName) => (
                                    <Badge key={teamName} variant="info">
                                      {teamName}
                                    </Badge>
                                  ))
                                ) : (
                                  <Badge variant="info">Assigned</Badge>
                                )}
                              </div>
                            ) : (
                              <Badge variant={needsTeam ? 'warning' : 'secondary'}>
                                Needs team
                              </Badge>
                            )}
                          </td>
                          <td className="w-10 px-2 py-2.5 text-right align-top">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveRole(index)}
                              aria-label={`Remove ${assignment.email || 'role assignment'}`}
                              className="opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive focus-visible:opacity-100 group-hover:opacity-100"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="teams" className="space-y-4">
        <Card className="bg-surface-0" style={{ boxShadow: 'var(--shadow-1)' }}>
          <CardHeader className="space-y-2">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-primary" />
                  <CardTitle>VP Email Management</CardTitle>
                </div>
                <CardDescription>
                  Teams and policy ownership are synced from the Snowflake team-to-policy mart.
                  Assign VP emails to the teams they should be allowed to view. The same email can
                  be assigned to multiple teams, granting that VP access to every team it appears
                  on.
                </CardDescription>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  size="lg"
                  onClick={handleSaveMappings}
                  disabled={!isDirty || validation.hasErrors || saveMappingsMutation.isPending}
                  style={{ backgroundColor: 'hsl(211 47% 20%)' }}
                  className="min-w-[10.5rem] text-white shadow-sm hover:opacity-90"
                >
                  {saveMappingsMutation.isPending ? 'Saving...' : 'Save Assignments'}
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            {saveMappingsMutation.isError && (
              <StatusBanner
                variant="error"
                title={
                  saveMappingsMutation.error instanceof Error
                    ? saveMappingsMutation.error.message
                    : 'Failed to save VP email assignments'
                }
              />
            )}

            {teams.length === 0 ? (
              <div className="rounded-lg border border-dashed bg-muted/20 px-4 py-10 text-center">
                <p className="text-sm text-muted-foreground">
                  No policy sales teams are available yet. Check the policy sync and Snowflake view
                  access.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                {teams.map((team) => {
                  const rawValue = draftEmailsByTeam[team.salesTeamId] ?? '';
                  const draftEmails = parseEmailTokens(rawValue);
                  const invalidEmails = validation.invalidByTeam[team.salesTeamId] ?? [];
                  const isEditing = editingTeam === team.salesTeamId;

                  return (
                    <Card
                      key={team.salesTeamId}
                      className="border-border/70 bg-surface-2"
                      style={{ boxShadow: 'var(--shadow-1)' }}
                    >
                      <CardHeader className="space-y-2 pb-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="space-y-1">
                            <CardTitle className="text-base">{team.salesTeamName}</CardTitle>
                            <CardDescription>
                              Team ID: <span className="font-mono">{team.salesTeamId}</span>
                            </CardDescription>
                          </div>

                          <div className="flex flex-wrap justify-end gap-2">
                            <Badge variant="info" className="px-2.5">
                              {team.policyCount} policies
                            </Badge>
                            <Badge variant="secondary" className="px-2.5">
                              {draftEmails.length} VP emails
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>

                      <CardContent className="space-y-3">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                              VP emails
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              Comma-separated while editing
                            </span>
                          </div>

                          {isEditing ? (
                            <Input
                              autoFocus
                              value={rawValue}
                              onChange={(event) =>
                                handleDraftChange(team.salesTeamId, event.target.value)
                              }
                              onBlur={() =>
                                setEditingTeam((current) =>
                                  current === team.salesTeamId ? null : current
                                )
                              }
                              placeholder="vp1@figmarketing.com, vp2@figmarketing.com"
                              className="h-9 bg-background"
                            />
                          ) : (
                            <button
                              type="button"
                              onClick={() => setEditingTeam(team.salesTeamId)}
                              className="flex min-h-9 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2 text-left shadow-sm transition-colors hover:border-primary/35 hover:bg-muted/40"
                            >
                              {draftEmails.length > 0 ? (
                                draftEmails.map((email) => (
                                  <Badge
                                    key={email}
                                    variant="secondary"
                                    className="bg-primary/[0.08] text-foreground"
                                  >
                                    {email}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-sm text-muted-foreground">
                                  Click to add VP emails
                                </span>
                              )}
                            </button>
                          )}

                          {invalidEmails.length > 0 && (
                            <div className="space-y-1 rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                              <p>Invalid: {invalidEmails.join(', ')}</p>
                            </div>
                          )}
                        </div>

                        <Separator />

                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                            <Database className="h-3.5 w-3.5" />
                            Policy Preview
                          </div>

                          <div className="flex max-h-36 flex-wrap gap-1.5 overflow-y-auto rounded-md border border-border/60 bg-muted/[0.18] p-2">
                            {team.policyIdPreview.length > 0 ? (
                              team.policyIdPreview.map((policyId) => (
                                <Badge
                                  key={policyId}
                                  variant="outline"
                                  className="border-border/60 bg-background font-mono text-[10px] text-muted-foreground"
                                >
                                  {policyId}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                No policy preview available
                              </span>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="syncs" className="space-y-4 pt-2">
        <Card className="bg-surface-0" style={{ boxShadow: 'var(--shadow-1)' }}>
          <CardContent className="space-y-4 pt-6">
            <div className="space-y-1">
              <h2 className="text-lg font-semibold">Scheduled Automation</h2>
              <p className="text-sm text-muted-foreground">
                This tab tracks the recurring jobs configured in Terraform: hourly ARW ingestion and
                the 6:15 AM / 12:15 PM ET policy scoring, policy sales-team, and Firelight mapping
                syncs.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3 xl:grid-cols-4">
              <Card
                className="border-border/70 bg-surface-2"
                style={{ boxShadow: 'var(--shadow-1)' }}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10">
                      <Download className="h-4 w-4 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <TruncatedTooltip
                        text="Hourly ARW Ingestion"
                        className="text-xs font-medium text-muted-foreground"
                      />
                      <div className="mt-0.5 flex items-baseline gap-2">
                        <p className="font-tabular text-xl font-semibold tracking-tight">
                          {formatRelativeTime(latestArwRun?.started_at ?? null)}
                        </p>
                      </div>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        Hourly · {arwStatus.label}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card
                className="border-border/70 bg-surface-2"
                style={{ boxShadow: 'var(--shadow-1)' }}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10">
                      <Database className="h-4 w-4 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <TruncatedTooltip
                        text="Policy Refresh + Scoring"
                        className="text-xs font-medium text-muted-foreground"
                      />
                      <div className="mt-0.5 flex items-baseline gap-2">
                        <p className="font-tabular text-xl font-semibold tracking-tight">
                          {formatRelativeTime(
                            (latestScoringRun?.started_at ??
                              syncStatus?.policies.lastSyncAt ??
                              null) as string | null
                          )}
                        </p>
                      </div>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        6:15 / 12:15 ET · {policyStatus.label}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card
                className="border-border/70 bg-surface-2"
                style={{ boxShadow: 'var(--shadow-1)' }}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10">
                      <Mail className="h-4 w-4 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <TruncatedTooltip
                        text="Firelight/ARW Mapping"
                        className="text-xs font-medium text-muted-foreground"
                      />
                      <div className="mt-0.5 flex items-baseline gap-2">
                        <p className="font-tabular text-xl font-semibold tracking-tight">
                          {formatRelativeTime(
                            (latestFirelightMappingRun?.created_at ?? null) as string | null
                          )}
                        </p>
                      </div>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        6:15 / 12:15 ET · {firelightMappingStatus.label}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card
              className="border-border/70 bg-surface-2"
              style={{ boxShadow: 'var(--shadow-1)' }}
            >
              <CardHeader>
                <CardTitle>Scheduled Jobs</CardTitle>
                <CardDescription>
                  Only jobs that run automatically on a recurring cadence are shown here.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-primary/[0.04]">
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                          Job
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                          Schedule
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                          Last Run
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                          Status
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                          Notes
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {scheduledJobs.map((job) => (
                        <tr key={job.job} className="border-b last:border-0">
                          <td className="px-3 py-2">
                            <span className="font-medium">{job.job}</span>
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">{job.schedule}</td>
                          <td className="px-3 py-2 text-muted-foreground">{job.lastRun}</td>
                          <td className="px-3 py-2">
                            <Badge variant={job.status.variant} className="text-[10px]">
                              {job.status.label}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">{job.notes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              <Card
                className="border-border/70 bg-surface-2"
                style={{ boxShadow: 'var(--shadow-1)' }}
              >
                <CardHeader>
                  <CardTitle>Recent Policy Refresh Runs</CardTitle>
                  <CardDescription>
                    Latest policy, client, and party refreshes from the 6:15 / 12:15 ET scoring
                    pipeline.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto rounded-md border">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-primary/[0.04]">
                          <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                            Date
                          </th>
                          <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                            Type
                          </th>
                          <th className="px-3 py-2 text-right font-medium text-muted-foreground">
                            Records
                          </th>
                          <th className="px-3 py-2 text-right font-medium text-muted-foreground">
                            Duration
                          </th>
                          <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {dbStatus?.recentSyncHistory?.slice(0, 5).map((row, index) => {
                          const badge = getJobStatusBadge(
                            (row.status as string | undefined) ?? null
                          );
                          return (
                            <tr key={index} className="border-b last:border-0">
                              <td className="px-3 py-2 text-muted-foreground">
                                {formatDateTime(
                                  (row.created_at ?? row.started_at ?? row.startedAt ?? null) as
                                    | string
                                    | null
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <span className="font-medium capitalize">
                                  {String(row.entity_type ?? row.sync_type ?? row.syncType ?? '—')}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right font-tabular">
                                {formatNumber(
                                  (row.records_processed ?? row.recordsProcessed) as
                                    | number
                                    | null
                                    | undefined
                                )}
                              </td>
                              <td className="px-3 py-2 text-right font-tabular">
                                {(row.duration_ms ?? row.durationMs) != null
                                  ? formatDuration((row.duration_ms ?? row.durationMs) as number)
                                  : '—'}
                              </td>
                              <td className="px-3 py-2">
                                <Badge variant={badge.variant} className="text-[10px]">
                                  {badge.label}
                                </Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              <Card
                className="border-border/70 bg-surface-2"
                style={{ boxShadow: 'var(--shadow-1)' }}
              >
                <CardHeader>
                  <CardTitle>Recent ARW Ingestion Runs</CardTitle>
                  <CardDescription>
                    Latest hourly imports of product and carrier data from ARW.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto rounded-md border">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-primary/[0.04]">
                          <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                            Date
                          </th>
                          <th className="px-3 py-2 text-right font-medium text-muted-foreground">
                            Products
                          </th>
                          <th className="px-3 py-2 text-right font-medium text-muted-foreground">
                            Carriers
                          </th>
                          <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {dbStatus?.recentIngestionLogs?.slice(0, 5).map((row, index) => {
                          const badge = getJobStatusBadge(
                            (row.status as string | undefined) ?? null
                          );
                          return (
                            <tr key={index} className="border-b last:border-0">
                              <td className="px-3 py-2 text-muted-foreground">
                                {formatDateTime(
                                  (row.started_at ?? row.startedAt ?? null) as string | null
                                )}
                              </td>
                              <td className="px-3 py-2 text-right font-tabular">
                                {formatNumber(
                                  (row.products_processed ?? row.productsProcessed) as
                                    | number
                                    | null
                                    | undefined
                                )}
                              </td>
                              <td className="px-3 py-2 text-right font-tabular">
                                {formatNumber(
                                  (row.carriers_processed ?? row.carriersProcessed) as
                                    | number
                                    | null
                                    | undefined
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <Badge variant={badge.variant} className="text-[10px]">
                                  {badge.label}
                                </Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              <Card
                className="border-border/70 bg-surface-2"
                style={{ boxShadow: 'var(--shadow-1)' }}
              >
                <CardHeader>
                  <CardTitle>Recent Firelight/ARW Mapping Runs</CardTitle>
                  <CardDescription>
                    Latest product and carrier mapping syncs against FIG Products/Firelight data.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto rounded-md border">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-primary/[0.04]">
                          <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                            Date
                          </th>
                          <th className="px-3 py-2 text-right font-medium text-muted-foreground">
                            Products
                          </th>
                          <th className="px-3 py-2 text-right font-medium text-muted-foreground">
                            Mapped
                          </th>
                          <th className="px-3 py-2 text-right font-medium text-muted-foreground">
                            Duration
                          </th>
                          <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {dbStatus?.recentFirelightMappingRuns?.slice(0, 5).map((row, index) => {
                          const badge = getJobStatusBadge(
                            (row.status as string | undefined) ?? null
                          );
                          return (
                            <tr key={index} className="border-b last:border-0">
                              <td className="px-3 py-2 text-muted-foreground">
                                {formatDateTime(
                                  (row.created_at ?? row.started_at ?? row.startedAt ?? null) as
                                    | string
                                    | null
                                )}
                              </td>
                              <td className="px-3 py-2 text-right font-tabular">
                                {formatNumber(
                                  (row.records_processed ?? row.recordsProcessed) as
                                    | number
                                    | null
                                    | undefined
                                )}
                              </td>
                              <td className="px-3 py-2 text-right font-tabular">
                                {formatNumber(
                                  (row.records_inserted ?? row.recordsInserted) as
                                    | number
                                    | null
                                    | undefined
                                )}
                              </td>
                              <td className="px-3 py-2 text-right font-tabular">
                                {(row.duration_ms ?? row.durationMs) != null
                                  ? formatDuration((row.duration_ms ?? row.durationMs) as number)
                                  : '—'}
                              </td>
                              <td className="px-3 py-2">
                                <Badge variant={badge.variant} className="text-[10px]">
                                  {badge.label}
                                </Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card
              className="border-border/70 bg-surface-2"
              style={{ boxShadow: 'var(--shadow-1)' }}
            >
              <CardHeader>
                <CardTitle>Recent Post-Sync Scoring Runs</CardTitle>
                <CardDescription>
                  Logged scoring runs from the 6:15 / 12:15 ET policy refresh and scoring schedule.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-primary/[0.04]">
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                          Date
                        </th>
                        <th className="px-3 py-2 text-right font-medium text-muted-foreground">
                          Scored
                        </th>
                        <th className="px-3 py-2 text-right font-medium text-muted-foreground">
                          Duration
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {dbStatus?.recentScoringJobs?.slice(0, 5).map((row, index) => {
                        const badge = getJobStatusBadge((row.status as string | undefined) ?? null);
                        return (
                          <tr key={index} className="border-b last:border-0">
                            <td className="px-3 py-2 text-muted-foreground">
                              {formatDateTime(
                                (row.started_at ?? row.startedAt ?? null) as string | null
                              )}
                            </td>
                            <td className="px-3 py-2 text-right font-tabular">
                              {formatNumber(
                                (row.policies_scored ?? row.policiesScored) as
                                  | number
                                  | null
                                  | undefined
                              )}
                            </td>
                            <td className="px-3 py-2 text-right font-tabular">
                              {(row.processing_time_ms ?? row.processingTimeMs) != null
                                ? formatDuration(
                                    (row.processing_time_ms ?? row.processingTimeMs) as number
                                  )
                                : '—'}
                            </td>
                            <td className="px-3 py-2">
                              <Badge variant={badge.variant} className="text-[10px]">
                                {badge.label}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="health" className="space-y-4 pt-2">
        <Card className="bg-surface-0" style={{ boxShadow: 'var(--shadow-1)' }}>
          <CardHeader className="space-y-2">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-primary" />
                  <CardTitle>Health Console</CardTitle>
                </div>
                <CardDescription>
                  Live in-memory endpoint timings for this API process. Use this to spot slow pages,
                  slow backend routes, and erroring endpoints after deploys.
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => refetchHealth()}>
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {healthError ? (
              <StatusBanner
                variant="error"
                title={
                  healthError instanceof Error
                    ? healthError.message
                    : 'Failed to load health metrics'
                }
              />
            ) : healthLoading ? (
              <div className="rounded-lg border border-dashed bg-muted/20 px-4 py-10 text-center">
                <p className="text-sm text-muted-foreground">Loading endpoint health metrics...</p>
              </div>
            ) : (
              <HealthConsoleTable data={healthConsole} />
            )}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="prefill" className="space-y-4 pt-2">
        <FirelightPrefillTab />
      </TabsContent>
    </Tabs>
  );
}

function HealthConsoleTable({ data }: { data: ReturnType<typeof useHealthConsole>['data'] }) {
  const endpoints = data?.endpoints ?? [];
  const slowest = endpoints[0];
  const totalRequests = endpoints.reduce((sum, endpoint) => sum + endpoint.count, 0);
  const totalErrors = endpoints.reduce((sum, endpoint) => sum + endpoint.errorCount, 0);

  if (!data || endpoints.length === 0) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/20 px-4 py-10 text-center">
        <p className="text-sm text-muted-foreground">
          No request metrics have been recorded yet. Navigate around the app, then refresh this tab.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <HealthMetricCard label="Requests" value={formatNumber(totalRequests)} />
        <HealthMetricCard label="Endpoints" value={formatNumber(endpoints.length)} />
        <HealthMetricCard label="Errors" value={formatNumber(totalErrors)} tone="danger" />
        <HealthMetricCard
          label="Slowest P95"
          value={slowest ? formatDuration(slowest.p95Ms) : '—'}
          detail={slowest?.route}
          tone={slowest && slowest.p95Ms > 1000 ? 'warning' : 'default'}
        />
      </div>

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-primary/[0.04]">
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Endpoint</th>
              <th className="px-3 py-2 text-right font-medium text-muted-foreground">Count</th>
              <th className="px-3 py-2 text-right font-medium text-muted-foreground">P95</th>
              <th className="px-3 py-2 text-right font-medium text-muted-foreground">Avg</th>
              <th className="px-3 py-2 text-right font-medium text-muted-foreground">Max</th>
              <th className="px-3 py-2 text-right font-medium text-muted-foreground">Last</th>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Status</th>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Last Seen</th>
            </tr>
          </thead>
          <tbody>
            {endpoints.map((endpoint) => {
              const badge =
                endpoint.errorCount > 0
                  ? { label: `${endpoint.errorCount} errors`, variant: 'destructive' as const }
                  : endpoint.p95Ms > 1000
                    ? { label: 'Slow', variant: 'warning' as const }
                    : { label: 'Healthy', variant: 'success' as const };

              return (
                <tr key={`${endpoint.method}-${endpoint.route}`} className="border-b last:border-0">
                  <td className="max-w-[28rem] px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="font-mono text-[10px]">
                        {endpoint.method}
                      </Badge>
                      <TruncatedTooltip text={endpoint.route} className="font-mono text-[11px]" />
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-tabular">
                    {formatNumber(endpoint.count)}
                  </td>
                  <td className="px-3 py-2 text-right font-tabular">
                    {formatDuration(endpoint.p95Ms)}
                  </td>
                  <td className="px-3 py-2 text-right font-tabular">
                    {formatDuration(endpoint.avgMs)}
                  </td>
                  <td className="px-3 py-2 text-right font-tabular">
                    {formatDuration(endpoint.maxMs)}
                  </td>
                  <td className="px-3 py-2 text-right font-tabular">
                    {formatDuration(endpoint.lastMs)}
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant={badge.variant} className="text-[10px]">
                      {badge.label}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {formatRelativeTime(endpoint.lastSeenAt)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted-foreground">
        Metrics reset whenever the API process restarts. Started {formatDateTime(data.startedAt)}.
      </p>
    </div>
  );
}

function HealthMetricCard({
  label,
  value,
  detail,
  tone = 'default',
}: {
  label: string;
  value: string;
  detail?: string;
  tone?: 'default' | 'warning' | 'danger';
}) {
  const toneClass =
    tone === 'danger'
      ? 'border-destructive/25 bg-destructive/5'
      : tone === 'warning'
        ? 'border-warning/25 bg-warning/5'
        : 'border-border/70 bg-surface-2';

  return (
    <Card className={cn(toneClass)} style={{ boxShadow: 'var(--shadow-1)' }}>
      <CardContent className="p-4">
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        <p className="mt-1 font-tabular text-2xl font-semibold tracking-tight">{value}</p>
        {detail && (
          <TruncatedTooltip text={detail} className="mt-1 text-[11px] text-muted-foreground" />
        )}
      </CardContent>
    </Card>
  );
}

function buildDraftEmailsByTeam(teams: AdminManagedTeam[]): Record<string, string> {
  return Object.fromEntries(
    teams.map((team) => [team.salesTeamId, team.assignedEmails.join(', ')])
  );
}

function buildDraftRoles(roles: AdminRoleAssignment[]): DraftRoleAssignment[] {
  return roles.map((assignment, index) => ({
    draftId: `saved-role-${assignment.email}-${index}`,
    email: assignment.email,
    role: assignment.role,
    hasTeamAssignment: assignment.hasTeamAssignment,
    salesTeamNames: assignment.salesTeamNames,
  }));
}

function createDraftRoleId(): string {
  draftRoleIdCounter += 1;
  return `new-role-${Date.now()}-${draftRoleIdCounter}`;
}

function serializeRoleAssignments(
  roles: Array<Pick<DraftRoleAssignment, 'email' | 'role'>>
): string {
  return roles
    .map((assignment) => ({
      email: normalizeEmail(assignment.email) ?? '',
      role: assignment.role,
    }))
    .sort((a, b) => a.email.localeCompare(b.email))
    .map((assignment) => `${assignment.email}:${assignment.role}`)
    .join(',');
}

function parseEmailTokens(rawValue: string): string[] {
  return Array.from(
    new Set(
      rawValue
        .split(',')
        .map((token) => normalizeEmail(token))
        .filter((token): token is string => Boolean(token))
    )
  ).sort((a, b) => a.localeCompare(b));
}

function normalizeEmail(value: string): string | undefined {
  const normalized = value.trim().toLowerCase();
  return normalized ? normalized : undefined;
}

function serializeEmails(emails: string[]): string {
  return [...emails].sort((a, b) => a.localeCompare(b)).join(',');
}

function validateTeamEmails(
  teams: AdminManagedTeam[],
  draftEmailsByTeam: Record<string, string>
): {
  invalidByTeam: Record<string, string[]>;
  hasErrors: boolean;
} {
  const invalidByTeam: Record<string, string[]> = {};

  // The same email may be assigned to multiple teams, so only invalid email
  // formats are treated as errors here.
  for (const team of teams) {
    const emails = parseEmailTokens(draftEmailsByTeam[team.salesTeamId] ?? '');
    const invalid = emails.filter((email) => !EMAIL_REGEX.test(email));

    if (invalid.length > 0) {
      invalidByTeam[team.salesTeamId] = invalid;
    }
  }

  return {
    invalidByTeam,
    hasErrors: Object.keys(invalidByTeam).length > 0,
  };
}

function validateRoleAssignments(roles: DraftRoleAssignment[]): {
  invalidEmails: string[];
  duplicateEmails: string[];
  hasErrors: boolean;
} {
  const invalidEmails: string[] = [];
  const duplicateEmails: string[] = [];
  const counts = new Map<string, number>();
  let adminCount = 0;

  for (const assignment of roles) {
    const email = normalizeEmail(assignment.email) ?? '';
    if (!EMAIL_REGEX.test(email)) {
      invalidEmails.push(email);
      continue;
    }

    counts.set(email, (counts.get(email) ?? 0) + 1);

    if (assignment.role === 'admin') {
      adminCount += 1;
    }
  }

  for (const [email, count] of counts.entries()) {
    if (count > 1) {
      duplicateEmails.push(email);
    }
  }

  if (adminCount === 0) {
    invalidEmails.push('At least one admin is required.');
  }

  return {
    invalidEmails,
    duplicateEmails,
    hasErrors: invalidEmails.length > 0 || duplicateEmails.length > 0,
  };
}

function getLatestSyncRun(
  rows: Array<Record<string, unknown>>,
  entityType: string
): Record<string, unknown> | null {
  return (
    rows.find(
      (row) =>
        String(row.entity_type ?? row.sync_type ?? row.syncType ?? '').toLowerCase() ===
        entityType.toLowerCase()
    ) ?? null
  );
}

function getJobStatusBadge(status: string | null | undefined): {
  label: string;
  variant: SyncBadgeVariant;
} {
  const normalized = status?.trim().toLowerCase();

  if (!normalized) {
    return { label: 'No data', variant: 'secondary' };
  }

  if (normalized === 'completed' || normalized === 'success') {
    return { label: 'Completed', variant: 'success' };
  }

  if (normalized === 'failed') {
    return { label: 'Failed', variant: 'destructive' };
  }

  if (normalized === 'running') {
    return { label: 'Running', variant: 'warning' };
  }

  return {
    label: normalized.charAt(0).toUpperCase() + normalized.slice(1),
    variant: 'secondary',
  };
}
